import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { openDownload } from "@/lib/downloads";
import { TechGuideButton } from "@/components/TechGuide";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Play, CheckCircle2, ExternalLink, Upload, Camera, Clock,
  ArrowLeft, FileText, Link2, PackageSearch, AlertTriangle, Download,
  StopCircle, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-slate-500/15 border-slate-500/30 text-slate-300",
  in_progress: "bg-amber-500/15 border-amber-500/30 text-amber-300",
  completed: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300",
  skipped: "bg-rose-500/15 border-rose-500/30 text-rose-300",
};

function formatDuration(card) {
  let secs = card?.duration_seconds || 0;
  if (!secs && card?.started_at && !card?.finished_at) {
    secs = Math.floor((Date.now() - new Date(card.started_at).getTime()) / 1000);
  }
  if (!secs) return "0m";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function WorkOrder() {
  const { id } = useParams();
  const [wo, setWo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const r = await api.get(`/work-orders/${id}`);
      setWo(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  // Tick every 5s so timers re-render when something is in progress
  useEffect(() => {
    const inProgress = wo?.execution?.status === "in_progress";
    if (!inProgress) return;
    const t = setInterval(() => setTick((x) => x + 1), 5000);
    return () => clearInterval(t);
  }, [wo?.execution?.status]);

  const startWo = async () => {
    try { await api.post(`/work-orders/${id}/start`); toast.success("Work order started"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const finishWo = async () => {
    if (!window.confirm("Finish this Work Order? This marks calibration as completed.")) return;
    try { await api.post(`/work-orders/${id}/finish`); toast.success("Work order completed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const claimWo = async () => {
    try { await api.post(`/work-orders/${id}/claim`); toast.success("Claimed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="flex justify-center items-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>;
  if (!wo) return <div className="p-6 text-slate-400">Work order not found.</div>;

  const veh = wo.report?.vehicle || {};
  const claim = wo.report?.claim || {};
  const exe = wo.execution || {};
  const sections = wo.report?.sections || [];

  const totalCards = sections.reduce((acc, s) => acc + (s.cards?.length || 0), 0);
  const completedCards = sections.reduce(
    (acc, s) => acc + (s.cards || []).filter((c) => c.execution?.status === "completed").length, 0,
  );
  const progressPct = totalCards ? Math.round((completedCards / totalCards) * 100) : 0;

  const openBundle = async () => {
    await openDownload(`/api/work-orders/${id}/bundle.pdf`, "work_order_bundle_pdf", id);
  };

  return (
    <div className="space-y-4 pb-20" data-testid="work-order-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Link to="/work-orders" className="text-slate-400 hover:text-slate-200 text-sm flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to Queue
        </Link>
        <div className="flex items-center gap-2">
          <button type="button" onClick={openBundle} data-testid="wo-download-bundle">
            <Button variant="outline" size="sm" className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10" asChild>
              <span><Download className="w-3.5 h-3.5 mr-1.5" /> Evidence Bundle</span>
            </Button>
          </button>
        </div>
      </div>

      {/* Vehicle / Claim header */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-slate-500">Work Order #{id.slice(0, 8)}</div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-100 mt-1" data-testid="wo-vehicle">
              {veh.label || "Vehicle"}
            </h1>
            <div className="text-xs text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-1">
              {veh.vin && <span>VIN <span className="text-slate-200 font-mono">{veh.vin}</span></span>}
              {claim.ro_number && <span>RO #{claim.ro_number}</span>}
              {claim.insurance && <span>{claim.insurance}</span>}
              {claim.claim_number && <span>Claim #{claim.claim_number}</span>}
            </div>
          </div>
          <Badge variant="outline" className={STATUS_COLORS[exe.status || "pending"]} data-testid="wo-status">
            {exe.status || "pending"}
          </Badge>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
            <span>{completedCards} of {totalCards} operations</span>
            <span>{progressPct}%</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all"
              style={{ width: `${progressPct}%` }}
              data-testid="wo-progress-bar"
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex flex-wrap gap-2">
          {!exe.assigned_tech_id && (
            <Button onClick={claimWo} size="sm" className="bg-blue-600 hover:bg-blue-500" data-testid="wo-claim-btn">
              Claim
            </Button>
          )}
          {exe.assigned_tech_id && (
            <span className="text-xs text-slate-400 flex items-center gap-1">
              Assigned to <span className="text-slate-200">{exe.assigned_tech_name}</span>
            </span>
          )}
          {exe.status !== "in_progress" && exe.status !== "completed" && (
            <Button onClick={startWo} size="sm" className="bg-emerald-600 hover:bg-emerald-500" data-testid="wo-start-btn">
              <Play className="w-3.5 h-3.5 mr-1.5" /> Start Work Order
            </Button>
          )}
          {exe.status === "in_progress" && (
            <Button onClick={finishWo} size="sm" className="bg-blue-600 hover:bg-blue-500" data-testid="wo-finish-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Finish Work Order
            </Button>
          )}
          {exe.started_at && (
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Started {new Date(exe.started_at).toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Overall pre/post scans */}
      <OverallScans wo={wo} onChange={load} />

      {/* Sections */}
      {sections.map((sec) => (
        sec.cards.length > 0 && (
          <section key={sec.label}>
            <h2 className="text-sm uppercase tracking-wider text-slate-500 mb-2">{sec.label}</h2>
            <div className="space-y-3">
              {sec.cards.map((card) => (
                <OperationCard key={card.id} card={card} reportId={id} jobId={wo?.job_id} onChange={load} />
              ))}
            </div>
          </section>
        )
      ))}

      <div className="hidden">{tick}</div>
    </div>
  );
}

function OverallScans({ wo, onChange }) {
  const exe = wo.execution || {};
  const preInputRef = useRef(null);
  const postInputRef = useRef(null);

  const upload = async (scanType, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("scan_type", scanType);
    fd.append("file", file);
    try {
      await api.post(`/work-orders/${wo.id}/scan`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Overall ${scanType}-scan uploaded`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
      <div className="text-sm uppercase tracking-wider text-slate-500 mb-3">Overall Scans</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="border border-slate-800 rounded-lg p-3 bg-slate-950/40">
          <div className="text-xs text-slate-400 mb-2">Pre-scan PDF</div>
          {exe.overall_scan_pre_url ? (
            <button
              type="button"
              onClick={() => openDownload(exe.overall_scan_pre_url, "work_order_scan_file", `${wo.id}:pre`)}
              className="text-xs text-blue-400 hover:underline flex items-center gap-1"
              data-testid="overall-pre-link"
            >
              <FileText className="w-3.5 h-3.5" /> View pre-scan
            </button>
          ) : (
            <span className="text-xs text-slate-500">Not uploaded</span>
          )}
          <input ref={preInputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => upload("pre", e.target.files?.[0])} data-testid="overall-pre-file" />
          <Button size="sm" variant="outline" className="mt-2 border-slate-700" onClick={() => preInputRef.current?.click()} data-testid="overall-pre-upload">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> {exe.overall_scan_pre_url ? "Replace" : "Upload"}
          </Button>
        </div>
        <div className="border border-slate-800 rounded-lg p-3 bg-slate-950/40">
          <div className="text-xs text-slate-400 mb-2">Post-scan PDF</div>
          {exe.overall_scan_post_url ? (
            <button
              type="button"
              onClick={() => openDownload(exe.overall_scan_post_url, "work_order_scan_file", `${wo.id}:post`)}
              className="text-xs text-blue-400 hover:underline flex items-center gap-1"
              data-testid="overall-post-link"
            >
              <FileText className="w-3.5 h-3.5" /> View post-scan
            </button>
          ) : (
            <span className="text-xs text-slate-500">Not uploaded</span>
          )}
          <input ref={postInputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => upload("post", e.target.files?.[0])} data-testid="overall-post-file" />
          <Button size="sm" variant="outline" className="mt-2 border-slate-700" onClick={() => postInputRef.current?.click()} data-testid="overall-post-upload">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> {exe.overall_scan_post_url ? "Replace" : "Upload"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function OperationCard({ card, reportId, jobId, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(card.execution?.notes || "");
  const [alldataUrl, setAlldataUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const preInputRef = useRef(null);
  const postInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const alldataPdfInputRef = useRef(null);
  const cardExec = card.execution || { status: "pending" };
  const links = card.links || [];

  const start = async () => {
    try { await api.post(`/work-orders/${reportId}/cards/${card.id}/start`); toast.success("Started"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const finish = async () => {
    try { await api.post(`/work-orders/${reportId}/cards/${card.id}/finish`); toast.success("Completed"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const skip = async () => {
    const reason = window.prompt("Why are you skipping this operation?");
    if (reason === null) return;
    try { await api.post(`/work-orders/${reportId}/cards/${card.id}/skip`, { reason }); toast.success("Skipped"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveNotes = async () => {
    setSaving(true);
    try { await api.post(`/work-orders/${reportId}/cards/${card.id}/notes`, { notes }); toast.success("Saved"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const uploadScan = async (scanType, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("scan_type", scanType);
    fd.append("file", file);
    try { await api.post(`/work-orders/${reportId}/cards/${card.id}/scan`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${scanType}-scan uploaded`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("category", "operation");
    fd.append("file", file);
    try { await api.post(`/work-orders/${reportId}/cards/${card.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo added"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const pasteAlldata = async () => {
    if (!alldataUrl.trim()) return;
    try {
      await api.post(`/work-orders/${reportId}/cards/${card.id}/alldata`, {
        url: alldataUrl.trim(), label: `ALLDATA — ${card.name}`, source: "ALLDATA",
      });
      toast.success("ALLDATA link attached"); setAlldataUrl(""); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const status = cardExec.status || "pending";

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden" data-testid={`op-card-${card.id}`}>
      <div className="p-4 cursor-pointer" onClick={() => setExpanded((e) => !e)}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={STATUS_COLORS[status]}>{status}</Badge>
              {card.status && <span className="text-xs text-slate-500">{card.status}</span>}
              {cardExec.started_at && (
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {formatDuration(cardExec)}
                </span>
              )}
            </div>
            <h3 className="text-sm font-semibold text-slate-100 mt-1.5">{card.name}</h3>
            <div className="text-xs text-slate-400 mt-0.5">{card.procedure}</div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>

        {/* Inline ALLDATA link buttons + Iter 48 Tech Guide */}
        {(links.length > 0 || card.op_key) && (
          <div className="mt-2 flex flex-wrap gap-1.5 items-center" onClick={(e) => e.stopPropagation()}>
            {links.map((ln, i) => (
              <a key={i} href={ln.url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 hover:bg-blue-500/20"
                 data-testid={`op-alldata-link-${card.id}-${i}`}>
                <ExternalLink className="w-3 h-3" /> {ln.source}
              </a>
            ))}
            {(card.op_key || card.id) && jobId && (
              <TechGuideButton
                jobId={jobId}
                opKey={card.op_key || card.id}
                opName={card.name}
                calType={card.cal_type}
              />
            )}
          </div>
        )}
      </div>

      {expanded && (
        <div className="border-t border-slate-800 p-4 space-y-4 bg-slate-950/40">
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            {status !== "in_progress" && status !== "completed" && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500" onClick={start} data-testid={`op-start-${card.id}`}>
                <Play className="w-3 h-3 mr-1" /> Start
              </Button>
            )}
            {status === "in_progress" && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-500" onClick={finish} data-testid={`op-finish-${card.id}`}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Finish
              </Button>
            )}
            {status !== "completed" && status !== "skipped" && (
              <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10" onClick={skip} data-testid={`op-skip-${card.id}`}>
                <StopCircle className="w-3 h-3 mr-1" /> Skip
              </Button>
            )}
          </div>

          {/* OEM context */}
          <div className="grid sm:grid-cols-2 gap-3 text-xs">
            {card.systems?.length > 0 && (
              <div>
                <div className="text-slate-500 mb-1">Systems</div>
                <div className="text-slate-300">{card.systems.join(", ")}</div>
              </div>
            )}
            {card.tools?.length > 0 && (
              <div>
                <div className="text-slate-500 mb-1">Tools required</div>
                <div className="text-slate-300">{card.tools.join(", ")}</div>
              </div>
            )}
          </div>

          {/* Required equipment from inventory */}
          {card.suggested_equipment?.length > 0 && (
            <div>
              <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><PackageSearch className="w-3 h-3" /> Inventory match</div>
              <div className="flex flex-wrap gap-1.5">
                {card.suggested_equipment.map((eq) => (
                  <Link key={eq.id} to={`/equipment/${eq.id}`}
                        className="text-xs px-2 py-1 rounded-md bg-slate-800/80 border border-slate-700 text-slate-300 hover:border-blue-500/40"
                        data-testid={`op-equip-${card.id}-${eq.id}`}>
                    {eq.name} {eq.serial_number && <span className="text-slate-500">· {eq.serial_number}</span>}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* ALLDATA paste box */}
          <div className="border border-slate-800 rounded-lg p-3 bg-slate-900/40">
            <div className="text-xs text-slate-400 mb-1.5 flex items-center gap-1"><Link2 className="w-3 h-3" /> Paste ALLDATA URL</div>
            <div className="flex gap-2">
              <Input
                value={alldataUrl}
                onChange={(e) => setAlldataUrl(e.target.value)}
                placeholder="https://my.alldata.com/..."
                className="h-9 text-xs bg-slate-950 border-slate-700"
                data-testid={`op-alldata-input-${card.id}`}
              />
              <Button size="sm" onClick={pasteAlldata} disabled={!alldataUrl.trim()} className="bg-blue-600 hover:bg-blue-500" data-testid={`op-alldata-save-${card.id}`}>
                Attach
              </Button>
            </div>
          </div>

          {/* Scan + photo uploads */}
          <div className="grid sm:grid-cols-3 gap-2">
            <input ref={preInputRef} type="file" accept=".pdf,image/*" className="hidden"
                   onChange={(e) => uploadScan("pre", e.target.files?.[0])} data-testid={`op-pre-file-${card.id}`} />
            <Button size="sm" variant="outline" onClick={() => preInputRef.current?.click()}
                    className={`border-slate-700 ${cardExec.scan_pre_url ? "border-emerald-500/30 text-emerald-300" : ""}`}
                    data-testid={`op-pre-upload-${card.id}`}>
              <Upload className="w-3 h-3 mr-1.5" /> {cardExec.scan_pre_url ? "Pre ✓" : "Pre-scan"}
            </Button>
            <input ref={postInputRef} type="file" accept=".pdf,image/*" className="hidden"
                   onChange={(e) => uploadScan("post", e.target.files?.[0])} data-testid={`op-post-file-${card.id}`} />
            <Button size="sm" variant="outline" onClick={() => postInputRef.current?.click()}
                    className={`border-slate-700 ${cardExec.scan_post_url ? "border-emerald-500/30 text-emerald-300" : ""}`}
                    data-testid={`op-post-upload-${card.id}`}>
              <Upload className="w-3 h-3 mr-1.5" /> {cardExec.scan_post_url ? "Post ✓" : "Post-scan"}
            </Button>
            <input ref={photoInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                   onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid={`op-photo-file-${card.id}`} />
            <Button size="sm" variant="outline" onClick={() => photoInputRef.current?.click()}
                    className="border-slate-700" data-testid={`op-photo-${card.id}`}>
              <Camera className="w-3 h-3 mr-1.5" /> Photo ({cardExec.photos?.length || 0})
            </Button>
          </div>

          {/* Notes */}
          <div>
            <div className="text-xs text-slate-500 mb-1">Notes</div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Tech observations, DTCs, target setup..."
              rows={2}
              className="text-xs bg-slate-950 border-slate-700"
              data-testid={`op-notes-${card.id}`}
            />
            <div className="mt-1.5 flex justify-end">
              <Button size="sm" onClick={saveNotes} disabled={saving} variant="outline" className="border-slate-700 text-xs" data-testid={`op-notes-save-${card.id}`}>
                {saving ? "Saving..." : "Save notes"}
              </Button>
            </div>
          </div>

          {/* Warnings */}
          {card.locations?.includes("Front Bumper") && card.id === "millimeter_wave_radar" && (
            <div className="text-xs flex items-start gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Check OEM tolerance for radar mount damage within 300mm before calibration.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
